import { useDashboardContext } from '@/contexts/DashboardContext';
import {
  getDynamicPagePrimaryDomain,
  getDynamicPageRelevantDomains,
} from '@/utils';
import {
  Domain,
  DynamicPage,
  DynamicPageVariant,
  Website,
} from '@superfunnel/types';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { uid } from 'uid';
import { WebsiteUpdate } from '../../../contexts/WebsiteUpdaterContext';
import { EditorDevice } from '../EditorContext';

export interface UseEditorStateReturn {
  // Editor ID
  id: string;

  // Device state
  device: EditorDevice;
  setDevice: (device: EditorDevice) => void;

  // Website state
  website: Website | undefined;
  setWebsite: (website: Website | undefined) => void;
  activeWebsiteUpdate: WebsiteUpdate | undefined;
  setActiveWebsiteUpdate: (update: WebsiteUpdate | undefined) => void;

  // Website ID
  selectedWebsiteId: string;
  setSelectedWebsiteId: (id: string) => void;

  // Dynamic Page
  dynamicPageId: string | undefined;
  dynamicPage: DynamicPage | undefined;

  // Selected Variant
  selectedVariantId: string | undefined;
  selectedVariant: QueryDocumentSnapshot<DynamicPageVariant> | undefined;
  dynamicPageVariants: QueryDocumentSnapshot<DynamicPageVariant>[] | undefined;
  setDynamicPageVariants: (
    variants: QueryDocumentSnapshot<DynamicPageVariant>[] | undefined,
  ) => void;

  // UI state
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (
    collapsed: boolean | ((prev: boolean) => boolean),
  ) => void;
  previewMode: boolean;
  setPreviewMode: (previewMode: boolean | ((prev: boolean) => boolean)) => void;
  ready: number;
  setReady: () => void;
  resetReady: () => void;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;

  // Visual edit mode
  isVisualEditMode: boolean;
  setIsVisualEditMode: (mode: boolean) => void;

  // Editable state
  isEditable: boolean;
  setIsEditable: (editable: boolean) => void;

  // Analytics state
  showAnalytics: boolean;
  setShowAnalytics: (show: boolean) => void;

  // Unpublished changes state (for GlobalSection mode only)
  unpublishedChanges: boolean;
  setUnpublishedChanges: (hasChanges: boolean) => void;

  // Relevant domains
  primaryDomain: QueryDocumentSnapshot<Domain> | undefined;
  relevantDomains: QueryDocumentSnapshot<Domain>[] | undefined;
}

interface UseEditorStateGlobalSectionProps {
  mode: 'globalSection';
  globalSectionId: string;
  initialShowAnalytics?: boolean;
}

interface UseEditorStateWebsiteProps {
  mode: 'website';
  dynamicPageId: string;
  initialVariantId: string;
  initialWebsiteId: string;
  initialShowAnalytics?: boolean;
}

type UseEditorStateProps =
  | UseEditorStateGlobalSectionProps
  | UseEditorStateWebsiteProps;

export const useEditorState = (
  props: UseEditorStateProps,
): UseEditorStateReturn => {
  const { domains, dynamicPages } = useDashboardContext();
  const [device, setDevice] = useState<EditorDevice>('desktop');
  const [website, setWebsite] = useState<Website | undefined>();
  const [activeWebsiteUpdate, setActiveWebsiteUpdate] = useState<
    WebsiteUpdate | undefined
  >();
  const [selectedWebsiteId, setSelectedWebsiteId] = useState(
    props.mode === 'website' ? props.initialWebsiteId : props.globalSectionId,
  );
  const [_sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [previewMode, setPreviewMode] = useState(
    props.initialShowAnalytics ?? false,
  );
  const [readyCount, setReadyCount] = useState(0);
  const setReady = () => setReadyCount((prev) => prev + 1);
  const resetReady = () => setReadyCount(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isVisualEditMode, setIsVisualEditMode] = useState(false);
  const [isEditable, setIsEditable] = useState(true);
  const [showAnalytics, setShowAnalytics] = useState(
    props.initialShowAnalytics ?? false,
  );
  /** Track unpublished changes for GlobalSection mode only */
  const [unpublishedChanges, setUnpublishedChanges] = useState(false);
  const [dynamicPageVariants, setDynamicPageVariants] = useState<
    QueryDocumentSnapshot<DynamicPageVariant>[] | undefined
  >();

  const dynamicPage: DynamicPage | undefined = useMemo(
    () => {
      if (props.mode === 'website') {
        return dynamicPages
          ?.find((page) => page.id === props.dynamicPageId)
          ?.data();
      }
      return undefined;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dynamicPages, props.mode, (props as any).dynamicPageId],
  );

  // Generate a unique ID for this editor instance
  const id = useMemo(() => uid(8), []);

  const sidebarCollapsed = useMemo(
    () => props.mode === 'globalSection' || _sidebarCollapsed,
    [_sidebarCollapsed, props.mode],
  );

  const selectedVariant = useMemo(() => {
    return dynamicPageVariants?.find((variant) =>
      variant.data().websiteIds.includes(selectedWebsiteId ?? ''),
    );
  }, [dynamicPageVariants, selectedWebsiteId]);

  const selectedVariantId = useMemo(() => {
    return selectedVariant?.id;
  }, [selectedVariant]);

  const relevantDomains = useMemo(() => {
    if (!domains || !dynamicPage) return domains;

    return getDynamicPageRelevantDomains(domains, dynamicPage);
  }, [domains, dynamicPage]);

  const primaryDomain = useMemo(() => {
    if (!relevantDomains) return;

    return getDynamicPagePrimaryDomain(relevantDomains);
  }, [relevantDomains]);

  useEffect(() => {
    setSidebarCollapsed(previewMode);
  }, [previewMode]);

  // Auto-disable analytics when exiting preview mode
  useEffect(() => {
    if (!previewMode) {
      setShowAnalytics(false);
    }
  }, [previewMode]);

  return {
    // Editor ID
    id,

    // Device state
    device,
    setDevice,

    // Website state
    website,
    setWebsite,
    activeWebsiteUpdate,
    setActiveWebsiteUpdate,

    // Website ID
    selectedWebsiteId,
    setSelectedWebsiteId,

    // Dynamic Page
    dynamicPageId:
      props.mode === 'website'
        ? props.dynamicPageId
        : `globalSection_${props.globalSectionId}`,
    dynamicPage,

    // Selected Variant
    selectedVariantId,
    selectedVariant,
    dynamicPageVariants,
    setDynamicPageVariants,

    // UI state
    sidebarCollapsed,
    setSidebarCollapsed,
    previewMode,
    setPreviewMode,
    ready: readyCount,
    setReady,
    resetReady,
    sidebarOpen,
    setSidebarOpen,

    // Visual edit mode
    isVisualEditMode,
    setIsVisualEditMode,

    isEditable,
    setIsEditable,

    // Analytics state
    showAnalytics,
    setShowAnalytics,

    // Unpublished changes state (for GlobalSection mode only)
    unpublishedChanges,
    setUnpublishedChanges,

    // Relevant domains
    primaryDomain,
    relevantDomains,
  };
};
